if (!customElements.get('products-toolbar')) {
  class ProductsToolbar extends HTMLElement {
    constructor() {
      super();
      // Better practice: init after the element is connected to the DOM
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.init());
      } else {
        this.init();
      }
    }

    init() {
      // Cache DOM elements safely
      this.filtersComponent = document.querySelector('facet-filters');
      this.filtersColumn = document.querySelector('.main-products-grid__filters');
      this.layoutSwitcher = this.querySelector('.products-toolbar__layout');
      this.compareToggle = document.getElementById('compare-switch');
      this.sortBy = this.querySelector('.products-toolbar__sort');
      this.productsGrid = document.querySelector('.main-products-grid');

      // ==================== LAYOUT SWITCHER ====================
      if (this.layoutSwitcher) {
        this.layoutSwitcher.addEventListener('change', this.handleLayoutChange.bind(this));

        // Restore saved layout from localStorage
        const savedLayout = theme.storageUtil?.get('products-layout');
        if (savedLayout === 'list') {
          const listToggle = document.getElementById('list-layout-opt');
          if (listToggle) {
            listToggle.checked = true;
            // Trigger change so the rest of the logic runs
            listToggle.dispatchEvent(new Event('change', { bubbles: true }));
          } else {
            this.handleLayoutChange(null, 'list');
          }
        }
      }

      // ==================== COMPARE TOGGLE ====================
      if (this.compareToggle) {
        this.compareToggle.addEventListener('change', this.toggleCompare.bind(this));

        const savedCompare = theme.storageUtil?.get('compare-toggle');
        const initialChecked = savedCompare !== null ? savedCompare : this.compareToggle.checked;

        this.toggleCompare(null, initialChecked, false); // false = don't persist again on init

        // Add transition class after a small delay
        setTimeout(() => {
          this.classList.add('transition-ready');
          if (this.productsGrid) this.productsGrid.classList.add('transition-ready');
        }, 500);
      } 
      else if (theme.settings?.compareToggle === 'none') {
        this.toggleCompare(null, true, false);
      }

      // ==================== FILTERS TOGGLE ====================
      if (this.filtersColumn) {
        this.filterToggle = this.querySelector('.js-toggle-filters');
        if (this.filterToggle) {
          this.filterToggle.addEventListener('click', this.toggleFilters.bind(this));
        }
      }

      // ==================== SORT BY (when no facet-filters) ====================
      if (!this.filtersComponent && this.sortBy) {
        // Assuming custom-select dispatches a 'change' event with detail.selectedValue
        this.sortBy.addEventListener('change', ProductsToolbar.handleSortByChange);
      }
    }

    /**
     * Toggle Compare Mode
     */
    toggleCompare(evt, checked = null, persist = true) {
      const isChecked = evt ? evt.target.checked : checked;

      if (this.productsGrid) {
        this.productsGrid.dataset.compare = isChecked;
      }

      const compareButton = document.getElementById('compare-drawer-open');
      if (compareButton) {
        compareButton.classList.toggle('is-out', !isChecked);
      }

      // Sync checkbox if called programmatically
      if (!evt && this.compareToggle) {
        this.compareToggle.checked = !!isChecked;
      }

      if (persist && !Shopify.designMode && theme.storageUtil) {
        theme.storageUtil.set('compare-toggle', isChecked);
      }

      // Lazy-load compare resources if enabling compare
      if (isChecked) {
        const resourcesTag = document.getElementById('compare-resources');
        if (resourcesTag) {
          try {
            const resources = JSON.parse(resourcesTag.textContent);
            resources.forEach((resource) => {
              if (resource.type === 'js') {
                const script = document.createElement('script');
                script.src = resource.path;
                document.head.appendChild(script);
              } else if (resource.type === 'css') {
                const link = document.createElement('link');
                link.href = resource.path;
                link.rel = 'stylesheet';
                link.type = 'text/css';
                document.head.appendChild(link);
              }
            });
            resourcesTag.remove();
          } catch (e) {
            console.warn('Failed to load compare resources', e);
          }
        }
      }
    }

    /**
     * Toggle Filters (desktop sidebar)
     */
    toggleFilters() {
      if (!this.filterToggle) return;

      this.filterToggle.classList.toggle('is-active');
      const isOpen = this.filterToggle.classList.contains('is-active');

      this.filterToggle.setAttribute('aria-expanded', isOpen);

      if (theme.mediaMatches?.lg) {
        if (this.filtersColumn) {
          this.filtersColumn.classList.toggle('lg:js-hidden', !isOpen);
        }

        const productsList = document.querySelector('.main-products-grid__results > .grid');
        if (productsList) {
          productsList.className = isOpen
            ? (productsList.dataset.filtersOpenClasses || '')
            : (productsList.dataset.filtersClosedClasses || '');
        }
      } else {
        if (this.filtersComponent && typeof this.filtersComponent.open === 'function') {
          this.filtersComponent.open();
        }
      }
    }

    /**
     * Handle layout change (grid ↔ list)
     */
    handleLayoutChange(evt, layout) {
      const newLayout = layout || (evt && evt.target.value);
      if (!newLayout || !this.productsGrid) return;

      this.productsGrid.dataset.layout = newLayout;

      // Only save to storage when user actually changes it
      if (!layout && theme.storageUtil) {
        theme.storageUtil.set('products-layout', newLayout);
      }
    }

    /**
     * Static handler for sort-by when facet filters are not present
     */
    static handleSortByChange(evt) {
      if (!evt?.detail?.selectedValue) return;

      const urlObj = new URL(window.location.href);
      urlObj.searchParams.set('sort_by', evt.detail.selectedValue);
      urlObj.hash = 'products-toolbar';
      window.location.href = urlObj.toString();
    }
  }

  customElements.define('products-toolbar', ProductsToolbar);
}